import React, { useState } from 'react';
import { createOperator } from '../services/authService';
import CampoLimiteMonto, { limiteDe } from './CampoLimiteMonto';
import './OperatorModalStyles.css'; // Importamos los estilos específicos

// `verComo`: el superadmin da de alta en nombre del admin de esa cuenta (crearOperador).
function AddOperatorModal({ isOpen, onClose, onSuccess, currentUser, limiteCuenta, verComo }) {
    const [formData, setFormData] = useState({
        nombreCompleto: '',
        email: '',
        confirmarEmail: '',
        tienda: '',
        rol: 'operador',
        limite: ''
    });
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    if (!isOpen) return null;

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        // Validar que los emails coincidan
        if (formData.email !== formData.confirmarEmail) {
            setError('Los correos electrónicos no coinciden');
            setLoading(false);
            return;
        }

        // Los administradores ven todas las transferencias: el límite es solo para operadores.
        const limite = formData.rol === 'operador' ? limiteDe(formData.limite) : null;

        try {
            // En modo de prueba, simplemente simulamos el éxito
            const isTestMode = window.location.pathname.includes('test-modal');

            if (isTestMode) {
                // Simulamos un retraso para mostrar el estado de carga
                await new Promise(resolve => setTimeout(resolve, 1000));

                console.log('Datos del operador (modo prueba):', {
                    nombreCompleto: formData.nombreCompleto,
                    email: formData.email,
                    tienda: formData.tienda,
                    rol: formData.rol,
                    limite
                });

                // Pasar el email al callback de éxito, y el operador para mostrarlo en la lista de prueba
                onSuccess('Operador agregado exitosamente', formData.email, {
                    email: formData.email,
                    nombre: formData.nombreCompleto,
                    admin: formData.rol === 'administrador',
                    limite_monto: limite,
                    createdAt: new Date().toISOString()
                });
            } else {
                // Si el email ya está registrado, el backend lo rechaza y el catch de
                // abajo muestra el mensaje.

                // Usar el nombre completo directamente como fullName
                const userData = {
                    email: formData.email,
                    fullName: formData.nombreCompleto,  // Guardamos el nombre completo
                    businessName: currentUser?.businessName || '',  // Heredamos el businessName del usuario actual
                    tienda: formData.tienda,
                    admin: formData.rol === 'administrador',
                    limite,
                    verComo,
                    listado: true,
                    email_administrador: currentUser?.email || '',  // Añadimos el email del administrador que lo crea
                    createdAt: new Date().toISOString()  // Añadimos la fecha de creación
                };

                await createOperator(userData);

                // Pasar el email al callback de éxito
                onSuccess('Operador agregado exitosamente', formData.email);
            }

            setFormData({
                nombreCompleto: '',
                email: '',
                confirmarEmail: '',
                tienda: '',
                rol: 'operador',
                limite: ''
            });

            onClose();
        } catch (error) {
            console.error('Error al crear operador:', error);

            // Personalizar mensajes de error
            if (error.code === 'auth/email-already-in-use') {
                setError('Este correo electrónico ya está registrado. Por favor, utiliza otro correo.');
            } else {
                setError(error.message || 'Error al crear operador. Por favor, intenta nuevamente.');
            }
        } finally {
            setLoading(false);
        }
    };

    // Texto de descripción según el rol
    const getRoleDescription = (rol) => {
        if (rol === 'administrador') {
            return "Los administradores pueden crear operadores, asignarlos a distintas tiendas o áreas para identificarlos, y acceden a gestionar pagos y otras funciones de la cuenta";
        }
        return "Los operadores solo pueden ver las transferencias recibidas y no tienen acceso a gestionar ninguna otra función de la cuenta";
    };

    return (
        <div className="modal-overlay" onClick={(e) => {
            if (e.target === e.currentTarget) onClose();
        }}>
            <div className="modal-content add-operator-modal">
                <div className="modal-header">
                    <h2>Agregar Operador</h2>
                    <button className="close-button" onClick={onClose}>×</button>
                </div>

                <form onSubmit={handleSubmit} className="add-operator-form">
                    <div className="operator-form-group">
                        <label className="operator-form-label" htmlFor="nombreCompleto">
                            Nombre y apellido
                        </label>
                        <input
                            type="text"
                            name="nombreCompleto"
                            id="nombreCompleto"
                            value={formData.nombreCompleto}
                            onChange={handleChange}
                            required
                            className="operator-form-input"
                            placeholder="Ingresa el nombre completo"
                        />
                    </div>

                    <div className="operator-form-group">
                        <label className="operator-form-label" htmlFor="email">
                            Correo electrónico
                        </label>
                        <input
                            type="email"
                            name="email"
                            id="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="operator-form-input"
                            placeholder="Ingresa el correo electrónico"
                        />
                    </div>

                    <div className="operator-form-group">
                        <label className="operator-form-label" htmlFor="confirmarEmail">
                            Confirmar correo electrónico
                        </label>
                        <input
                            type="email"
                            name="confirmarEmail"
                            id="confirmarEmail"
                            value={formData.confirmarEmail}
                            onChange={handleChange}
                            required
                            className="operator-form-input"
                            placeholder="Confirma el correo electrónico"
                        />
                    </div>

                    <div className="operator-form-group">
                        <label className="operator-form-label" htmlFor="tienda">
                            Nombre de tienda o establecimiento
                        </label>
                        <input
                            type="text"
                            name="tienda"
                            id="tienda"
                            value={formData.tienda}
                            onChange={handleChange}
                            className="operator-form-input"
                            placeholder="Ingresa el nombre de la tienda"
                        />
                    </div>

                    <div className="operator-form-group">
                        <label className="operator-form-label" htmlFor="rol">
                            Rol
                        </label>
                        <select
                            name="rol"
                            id="rol"
                            value={formData.rol}
                            onChange={handleChange}
                            className="operator-form-input"
                        >
                            <option value="operador">Operador</option>
                            <option value="administrador">Administrador</option>
                        </select>
                        <p className="role-description">
                            {getRoleDescription(formData.rol)}
                        </p>
                    </div>

                    {formData.rol === 'operador' && (
                        <CampoLimiteMonto
                            id="limite"
                            valor={formData.limite}
                            onChange={(limite) => setFormData({ ...formData, limite })}
                            disabled={loading}
                            limiteCuenta={limiteCuenta}
                        />
                    )}

                    {error && <div className="error-message">{error}</div>}

                    <div className="form-actions">
                        <button
                            type="button"
                            onClick={onClose}
                            className="cancel-button"
                            disabled={loading}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="submit-button"
                            disabled={loading}
                        >
                            {loading ? 'Agregando...' : 'Agregar'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default AddOperatorModal;
